import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { supportedLanguages, getTranslation } from "./translations";

const LanguageContext = createContext();

function detectDefaultLanguage() {
  // Browser language like: "ar", "ar-SA", "en-US"
  const browserLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();

  // Prefer Arabic if browser language starts with "ar"
  if (browserLang.startsWith("ar")) return "ar";

  return "en";
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("geocheck_language");

    // If user already chose a language before, always respect it
    if (saved) return saved;

    // First visit only: detect from browser language
    const detected = detectDefaultLanguage();

    // Make sure detected language is supported; otherwise fallback to "en"
    const isSupported = supportedLanguages.some((l) => l.code === detected);
    const initial = isSupported ? detected : "en";

    // Save immediately so we don't flash English first
    localStorage.setItem("geocheck_language", initial);

    return initial;
  });

  const direction = useMemo(() => {
    const langConfig = supportedLanguages.find((l) => l.code === language);
    return langConfig?.dir || "ltr";
  }, [language]);

  useEffect(() => {
    localStorage.setItem("geocheck_language", language);

    // Keep document attributes synced (helps prevent Android auto-translate + ensures RTL)
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", language);
  }, [language, direction]);

  const t = (key) => getTranslation(language, key);

  const value = {
    language,
    setLanguage,
    direction,
    t,
    supportedLanguages,
    isRTL: direction === "rtl",
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export default LanguageContext;
